import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface CanvasTransform {
	scale: number;
	translateX: number;
	translateY: number;
}

export interface CanvasDimensions {
	containerWidth: number;
	containerHeight: number;
	canvasWidth: number;
	canvasHeight: number;
}

@Injectable({
	providedIn: 'root'
})
export class CanvasTransformService {
	private readonly SCROLL_SENSITIVITY = 0.5;
	private readonly ZOOM_SENSITIVITY = 0.01;
	private readonly MAX_SCALE = 2;

	private scale: number = 1;
	private translateX: number = 0;
	private translateY: number = 0;
	private BASE_SCALE: number = 1;
	private MIN_SCALE: number = 0.1;

	private dimensions: CanvasDimensions = {
		containerWidth: 0,
		containerHeight: 0,
		canvasWidth: 0,
		canvasHeight: 0
	};

	private transformSubject = new BehaviorSubject<CanvasTransform>({
		scale: this.scale,
		translateX: this.translateX,
		translateY: this.translateY
	});

	public transform$ = this.transformSubject.asObservable();

	setDimensions(dimensions: CanvasDimensions, dataLength: number, gridSize: number): void {
		this.dimensions = { ...dimensions };
		this.calculateMinScale(dataLength, gridSize);
	}

	getDimensions(): CanvasDimensions {
		return { ...this.dimensions };
	}

	getTransform(): CanvasTransform {
		return {
			scale: this.scale,
			translateX: this.translateX,
			translateY: this.translateY
		};
	}

	setInitialPosition(dataLength: number, gridSize: number): void {
		if (dataLength > 0) {
			let margin: number;
			if (dataLength < 4) {
				margin = 1;
			} else {
				margin = 4;
			}

			this.translateX = -((dataLength * gridSize) - margin * gridSize) * this.scale;
			this.translateY = -((5 * gridSize)) * this.scale;
			this.emitTransform();
		}
	}

	private calculateMinScale(dataLength: number, gridSize:number): void {
		const { containerWidth, containerHeight, canvasWidth, canvasHeight } = this.dimensions;

		if (!containerWidth || !containerHeight || !canvasWidth || !canvasHeight) {
			return;
		}
		//
		// let margin: number = 0
		// if( dataLength < 4) {
		// 	margin = (3*dataLength*gridSize);
		// } else if ( dataLength < 6) {
		// 	margin = (dataLength*gridSize);
		// } else {
		// 	margin = (8*gridSize);
		// }

		const baseScaleX = containerWidth / (canvasWidth - dataLength * (2 * gridSize));
		const baseScaleY = containerHeight / (canvasHeight - 7 * gridSize);
		const minScaleX = containerWidth / canvasWidth;
		const minScaleY = containerHeight / canvasHeight;

		this.BASE_SCALE = Math.min(baseScaleX, baseScaleY);
		this.MIN_SCALE = Math.min(minScaleX, minScaleY);
	}

	zoomIn(): void {
		this.zoomToContainerCenter(0.1);
	}

	zoomOut(): void {
		this.zoomToContainerCenter(-0.1);
	}

	recenter(): void {
		this.fitCanvasToContainer();
	}

	getZoomLevel(): number {
		return this.scale * 100;
	}

	private zoomToContainerCenter(zoomDelta: number): void {
		const newScale = this.scale * (1 + zoomDelta);

		if (newScale >= this.MIN_SCALE && newScale <= this.MAX_SCALE) {
			const { containerWidth, containerHeight } = this.dimensions;
			const centerX = containerWidth / 2;
			const centerY = containerHeight / 2;

			const newTranslateX = centerX - (centerX - this.translateX) * (newScale / this.scale);
			const newTranslateY = centerY - (centerY - this.translateY) * (newScale / this.scale);

			this.scale = newScale;
			this.applyTranslationLimits(newTranslateX, newTranslateY);
		}
	}

	zoomToPoint(zoomDelta: number, clientX: number, clientY: number, containerRect: DOMRect): void {
		const newScale = this.scale * (1 + zoomDelta);

		if (newScale >= this.MIN_SCALE && newScale <= this.MAX_SCALE) {
			const centerX = clientX - containerRect.left;
			const centerY = clientY - containerRect.top;

			const newTranslateX = centerX - (centerX - this.translateX) * (newScale / this.scale);
			const newTranslateY = centerY - (centerY - this.translateY) * (newScale / this.scale);

			this.scale = newScale;
			this.applyTranslationLimits(newTranslateX, newTranslateY);
		}
	}

	private fitCanvasToContainer(): void {
		this.scale = this.BASE_SCALE;
		this.centerCanvas();
	}

	private centerCanvas(): void {
		const { containerWidth, containerHeight, canvasWidth, canvasHeight } = this.dimensions;
		const scaledCanvasWidth = canvasWidth * this.scale;
		const scaledCanvasHeight = canvasHeight * this.scale;

		this.translateX = (containerWidth - scaledCanvasWidth) / 2;
		this.translateY = (containerHeight - scaledCanvasHeight) / 2;
		this.emitTransform();
	}

	pan(deltaX: number, deltaY: number): void {
		const newTranslateX = this.translateX + deltaX;
		const newTranslateY = this.translateY + deltaY;
		this.applyTranslationLimits(newTranslateX, newTranslateY);
	}

	scroll(deltaX: number, deltaY: number, isShift: boolean = false): void {
		if (isShift) {
			const newTranslateX = this.translateX - deltaY * this.SCROLL_SENSITIVITY;
			this.applyTranslationLimits(newTranslateX, this.translateY);
		} else {
			const newTranslateX = this.translateX - deltaX * this.SCROLL_SENSITIVITY;
			const newTranslateY = this.translateY - deltaY * this.SCROLL_SENSITIVITY;
			this.applyTranslationLimits(newTranslateX, newTranslateY);
		}
	}

	applyTranslationLimits(newX: number, newY: number): void {
		const { containerWidth, containerHeight, canvasWidth, canvasHeight } = this.dimensions;

		if (!containerWidth || !containerHeight || !canvasWidth || !canvasHeight) {
			return;
		}

		const scaledCanvasWidth = canvasWidth * this.scale;
		const scaledCanvasHeight = canvasHeight * this.scale;

		const minX = containerWidth - scaledCanvasWidth;
		const maxX = 0;
		const minY = containerHeight - scaledCanvasHeight;
		const maxY = 0;

		if (scaledCanvasWidth <= containerWidth) {
			this.translateX = (containerWidth - scaledCanvasWidth) / 2;
		} else {
			this.translateX = Math.min(maxX, Math.max(minX, newX));
		}

		if (scaledCanvasHeight <= containerHeight) {
			this.translateY = (containerHeight - scaledCanvasHeight) / 2;
		} else {
			this.translateY = Math.min(maxY, Math.max(minY, newY));
		}

		this.emitTransform();
	}

	handleResize(newContainerWidth: number, newContainerHeight: number, dataLength: number, gridSize: number): void {
		const previousWidth = this.dimensions.containerWidth;
		const previousHeight = this.dimensions.containerHeight;

		this.dimensions.containerWidth = newContainerWidth;
		this.dimensions.containerHeight = newContainerHeight;

		this.calculateMinScale(dataLength, gridSize);

		// Ajuster l'échelle si nécessaire
		if (this.scale < this.MIN_SCALE) {
			this.scale = this.MIN_SCALE;
		} else if (this.scale > this.MAX_SCALE) {
			this.scale = this.MAX_SCALE;
		}

		// Maintenir approximativement la même vue
		const centerXBefore = previousWidth / 2;
		const centerYBefore = previousHeight / 2;
		const centerXAfter = newContainerWidth / 2;
		const centerYAfter = newContainerHeight / 2;

		const deltaX = centerXAfter - centerXBefore;
		const deltaY = centerYAfter - centerYBefore;

		this.translateX += deltaX;
		this.translateY += deltaY;

		this.applyTranslationLimits(this.translateX, this.translateY);
	}

	reset(): void {
		this.scale = 0.85;
		this.translateX = 0;
		this.translateY = 0;
		this.emitTransform();
	}

	private emitTransform(): void {
		this.transformSubject.next({
			scale: this.scale,
			translateX: this.translateX,
			translateY: this.translateY
		});
	}

	// Méthodes utilitaires pour les scrollbars
	needsHorizontalScrollbar(): boolean {
		const { containerWidth, canvasWidth } = this.dimensions;
		return (canvasWidth * this.scale) > containerWidth;
	}

	needsVerticalScrollbar(): boolean {
		const { containerHeight, canvasHeight } = this.dimensions;
		return (canvasHeight * this.scale) > containerHeight;
	}

	getScrollbarData() {
		const { containerWidth, containerHeight, canvasWidth, canvasHeight } = this.dimensions;
		const scaledCanvasWidth = canvasWidth * this.scale;
		const scaledCanvasHeight = canvasHeight * this.scale;

		return {
			horizontal: {
				needed: this.needsHorizontalScrollbar(),
				scrollPercent: scaledCanvasWidth > containerWidth ?
					Math.abs(this.translateX) / (scaledCanvasWidth - containerWidth) : 0,
				thumbSize: scaledCanvasWidth > containerWidth ?
					(containerWidth / scaledCanvasWidth) : 1
			},
			vertical: {
				needed: this.needsVerticalScrollbar(),
				scrollPercent: scaledCanvasHeight > containerHeight ?
					Math.abs(this.translateY) / (scaledCanvasHeight - containerHeight) : 0,
				thumbSize: scaledCanvasHeight > containerHeight ?
					(containerHeight / scaledCanvasHeight) : 1
			}
		};
	}
}
